import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppBar, Box, IconButton, Popover, Typography } from "@mui/material";

import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { MasterConfirmDialog } from "../static/Masterpage";

const HEADER_COLOR = "#123B5D";
const HEADER_LIGHT = "#EAF2FB";

const BRAND_URL = "https://parivikshak.in/";

const BRAND_NAME = "Parivikshak";
const BRAND_HOST = BRAND_URL.replace(/^https?:\/\//, "").replace(/\/$/, "");

const ROUTES = {
  profile: "/userProfile",
};

const LINKS = [
  { icon: MailOutlineIcon, label: "Contact", href: "/contact-us" },
  {
    icon: LockOutlinedIcon,
    label: "Privacy policy",
    href: "/privacy",
  },
  {
    icon: DescriptionOutlinedIcon,
    label: "Terms of use",
    href: "/terms",
  },
];

const rowSx = {
  all: "unset",
  boxSizing: "border-box",
  width: "100%",
  height: 40,
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  px: 1.5,
  borderRadius: "8px",
  cursor: "pointer",
  fontSize: 14,
  color: "#1B2733",
  transition: "background-color .12s",
  "&:hover": { backgroundColor: "#EEF3F8" },
  "&:focus-visible": {
    outline: `2px solid ${HEADER_COLOR}`,
    outlineOffset: -2,
  },
};

const MenuRow = ({ icon: Icon, label, onClick, href, external, chevron }) => (
  <Box
    component={href ? "a" : "button"}
    {...(href
      ? { href, target: "_blank", rel: "noopener noreferrer" }
      : { type: "button", onClick })}
    sx={rowSx}
  >
    <Icon sx={{ fontSize: 19, color: HEADER_COLOR }} />
    <Box component="span" sx={{ flex: 1, textAlign: "left" }}>
      {label}
    </Box>
    {external && <OpenInNewIcon sx={{ fontSize: 14, color: "#A3AEB9" }} />}
    {chevron && <ChevronRightIcon sx={{ fontSize: 18, color: "#A3AEB9" }} />}
  </Box>
);

const SectionLabel = ({ children, first }) => (
  <Typography
    sx={{
      px: 1.5,
      pt: first ? 0.5 : 1.25,
      pb: 0.5,
      fontSize: 12,
      fontWeight: 500,
      color: "#7A8B9B",
    }}
  >
    {children}
  </Typography>
);

const LeftNavBar = ({ sidebarOpen, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [centerTitle, setCenterTitle] = useState("Organization");
  const [user, setUser] = useState({ name: "User", subtitle: "" });
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const profileMenuOpen = Boolean(profileAnchorEl);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("user");
      if (saved) {
        const u = JSON.parse(saved);
        setUser({
          name: u?.userid || "User",
          subtitle:
            u?.department || u?.role || u?.rolename || u?.roleName || "",
        });
      }
    } catch (error) {
      console.error("Failed to load user:", error);
    }
  }, []);

  useEffect(() => {
    const savedFunctions = localStorage.getItem("functions");
    if (!savedFunctions) {
      setCenterTitle("Organization");
      return;
    }
    try {
      const parsed = JSON.parse(savedFunctions);
      const active = parsed.find(
        (item) =>
          item.launch?.toLowerCase() === location.pathname.toLowerCase(),
      );
      setCenterTitle(active?.functionality?.trim() || "Organization");
    } catch (error) {
      console.error("Failed to read functions:", error);
      setCenterTitle("Organization");
    }
  }, [location.pathname]);

  // Click opens the menu, click again closes it (toggle)
  const handleProfileClick = (e) =>
    setProfileAnchorEl((prev) => (prev ? null : e.currentTarget));
  const handleProfileClose = () => setProfileAnchorEl(null);

  const goToProfile = () => {
    handleProfileClose();
    navigate(ROUTES.profile);
  };

  const handleLogoutClick = () => {
    handleProfileClose();
    setLogoutDialogOpen(true);
  };
  const cancelLogout = () => setLogoutDialogOpen(false);
  const confirmLogout = () => {
    localStorage.clear();
    navigate("/login", { replace: true });
    window.location.reload();
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          height: { xs: 56, sm: 64 },
          backgroundColor: HEADER_COLOR,
          backgroundImage: "none",
          color: "#FFFFFF",
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 1, sm: 1.5 },
            position: "relative",
          }}
        >
          {/* Sidebar toggle */}
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 42 }}>
            <IconButton
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
              sx={{
                width: 42,
                height: 42,
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

          {/* Page title (centred) */}
          <Typography
            component="h1"
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: { xs: "42%", sm: "40%", md: "55%" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: { xs: 15, sm: 18, md: 22 },
              fontWeight: 600,
              lineHeight: 1.2,
              color: "#FFFFFF",
              letterSpacing: "0.2px",
              pointerEvents: "none",
            }}
          >
            {centerTitle}
          </Typography>

          {/* User button: avatar + name + expand/collapse icon */}
          <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
            <Box
              component="button"
              type="button"
              onClick={handleProfileClick}
              aria-label="Open user menu"
              aria-haspopup="true"
              aria-expanded={profileMenuOpen}
              title={user.name}
              sx={{
                all: "unset",
                boxSizing: "border-box",
                height: 42,
                display: "inline-flex",
                alignItems: "center",
                gap: { xs: 0.25, sm: 0.75 },
                pl: 0.5,
                pr: { xs: 0.5, sm: 1 },
                borderRadius: "999px",
                cursor: "pointer",
                color: "#FFFFFF",
                border: "1px solid rgba(255,255,255,0.35)",
                backgroundColor: profileMenuOpen
                  ? "rgba(255,255,255,0.14)"
                  : "rgba(255,255,255,0.04)",
                transition: "background-color .15s, border-color .15s",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.10)",
                  borderColor: "rgba(255,255,255,0.55)",
                },
                "&:focus-visible": {
                  outline: "2px solid #FFFFFF",
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255,255,255,0.14)",
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 22 }} />
              </Box>

              {/* Name hidden on phones, shown from 600px up */}
              <Typography
                component="span"
                sx={{
                  display: { xs: "none", sm: "block" },
                  maxWidth: { sm: 90, md: 160 },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 14,
                  fontWeight: 600,
                  lineHeight: 1,
                  px: 0.5,
                }}
              >
                {user.name}
              </Typography>

              {profileMenuOpen ? (
                <ExpandLessIcon sx={{ fontSize: 20 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 20 }} />
              )}
            </Box>
          </Box>
        </Box>
      </AppBar>

      <Popover
        open={profileMenuOpen}
        anchorEl={profileAnchorEl}
        onClose={handleProfileClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 300,
            maxWidth: "calc(100vw - 16px)",
            maxHeight: "calc(100dvh - 80px)",
            overflowY: "auto",
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #D5DCE3",
            boxShadow: "0 12px 32px rgba(15,35,55,0.16)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 2,
            backgroundColor: HEADER_LIGHT,
            borderBottom: "1px solid #D9E4EF",
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              backgroundColor: "#FFFFFF",
              border: "1px solid rgba(18,59,93,0.20)",
              color: HEADER_COLOR,
              boxShadow: "0 2px 6px rgba(15,35,55,0.10)",
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 28 }} />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.3,
                color: HEADER_COLOR,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.name}
            </Typography>

            {user.subtitle && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.3,
                  color: "#5B7287",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user.subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* list */}
        <Box sx={{ pt: 1, px: 0.75, pb: 0.5 }}>
          <SectionLabel first>Account</SectionLabel>
          <MenuRow
            icon={AccountCircleOutlinedIcon}
            label="View profile"
            onClick={goToProfile}
            chevron
          />

          <SectionLabel>Help and legal</SectionLabel>
          {LINKS.map((l) => (
            <MenuRow
              key={l.label}
              icon={l.icon}
              label={l.label}
              href={l.href}
              external
            />
          ))}
        </Box>

        {/* logout */}
        <Box sx={{ px: 1.5, pt: 1, pb: 1.5 }}>
          <Box
            component="button"
            type="button"
            onClick={handleLogoutClick}
            sx={{
              all: "unset",
              boxSizing: "border-box",
              width: "100%",
              height: 38,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              borderRadius: "8px",
              border: "1px solid #E4B4B4",
              backgroundColor: "#FDF3F3",
              color: "#B3261E",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              transition: "background-color .12s",
              "&:hover": { backgroundColor: "#FBE6E6" },
              "&:focus-visible": {
                outline: "2px solid #B3261E",
                outlineOffset: 2,
              },
            }}
          >
            <LogoutIcon sx={{ fontSize: 17 }} />
            Log out
          </Box>
        </Box>

        {/* brand footer */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            backgroundColor: "#F7F9FB",
            borderTop: "1px solid #E3E8ED",
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#9AA3AB" }}>
            {BRAND_NAME}
          </Typography>

          <Box
            component="a"
            href={BRAND_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 13,
              fontWeight: 500,
              color: "#1565C0",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
              "&:focus-visible": {
                outline: "2px solid #1565C0",
                outlineOffset: 2,
                borderRadius: "4px",
              },
            }}
          >
            {BRAND_HOST}
            <NorthEastIcon sx={{ fontSize: 12 }} />
          </Box>
        </Box>
      </Popover>

      <MasterConfirmDialog
        show={logoutDialogOpen}
        title="Confirm Logout"
        message="Are you sure you want to log out?"
        onConfirm={confirmLogout}
        onCancel={cancelLogout}
      />
    </>
  );
};

export default LeftNavBar;
